import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { ThemeSwitcher } from '../pages/Home/components/ThemeSwitcher';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { FiMenu, FiChevronUp } from 'react-icons/fi';
import { useEffect, useState, useRef, useCallback } from 'react';
import { userService, User } from '../services/userService';
import { useTranslation } from 'react-i18next';
import '@/styles/cyberpunk.css';

export function Header() {
  const { user, signOut } = useAuth();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const lastScrollY = useRef(0);
  const { t } = useTranslation();
  const location = useLocation();

  // Check if we're in a tutorial page
  const isTutorialPage = location.pathname.includes('/learn/') && location.pathname.split('/').length > 3;

  // Handle scroll events with throttling to improve performance
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Show scroll-to-top button when scrolled down enough
    if (currentScrollY > 300) {
      setShowScrollButton(true);
    } else {
      setShowScrollButton(false);
    }
    
    // Apply background when scrolled down a little
    if (currentScrollY > 10) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
    
    // Auto-hiding: Hide header when scrolling down, show when scrolling up
    if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    
    lastScrollY.current = currentScrollY;
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (user?.id) {
      userService.getUserProfile(user.id)
        .then(profile => setUserProfile(profile))
        .catch(console.error);
    }
  }, [user?.id]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Render minimal header for tutorial pages
  if (isTutorialPage) {
    return null;
  }

  // Regular header for other pages
  return (
    <>
      {/* Main header with enhanced glassmorphism effect */}
      <header 
        className={`header-component fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-base-100/60 backdrop-blur-lg backdrop-saturate-150 shadow-sm' 
            : 'bg-base-100/20 backdrop-blur-sm'
        } ${
          hidden ? '-translate-y-full' : 'translate-y-0'
        }`}
        style={{
          WebkitBackdropFilter: scrolled ? 'blur(10px)' : 'none',
          backdropFilter: scrolled ? 'blur(10px)' : 'none'
        }}
      >
        {/* Decorative top border with animated gradient */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-primary to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/70 to-transparent animate-[moveLeftRight_4s_linear_infinite]"></div>
        </div>
        
        {/* Enhanced scanlines effect - lighter for better readability */}
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(138,75,255,0.03)_50%)] bg-[length:100%_3px] opacity-10 pointer-events-none"></div>
        
        {/* Content container with adjusted max-height for mobile */}
        <div className={`px-2 sm:px-4 flex items-center justify-between max-w-[100vw] relative transition-all duration-300 ${
          scrolled ? 'h-10 sm:h-12' : 'h-12 sm:h-16'
        }`}>
          <div className="flex items-center gap-2">
            {user && (
              <div className="dropdown">
                <label tabIndex={0} className="btn btn-ghost btn-sm lg:hidden p-1 hover:bg-primary/10 text-primary">
                  <FiMenu className="h-4 w-4" />
                </label>
                <ul tabIndex={0} className="menu menu-sm dropdown-content mt-2 z-[60] p-2 shadow-lg bg-base-200/90 backdrop-blur-md rounded-box w-52 border border-primary/30 overflow-hidden">
                  {/* Menu glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none"></div>
                  
                  {/* Decorative corner elements */}
                  <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-primary/60"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-primary/60"></div>
                  
                  <li>
                    <Link to="/learn" className="font-mono uppercase tracking-wide text-xs group hover:text-primary">
                      <span className="inline-block w-1 h-1 bg-primary mr-1 group-hover:w-2 transition-all"></span>
                      {t('nav.learn')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/challenges" className="font-mono uppercase tracking-wide text-xs group hover:text-primary">
                      <span className="inline-block w-1 h-1 bg-primary mr-1 group-hover:w-2 transition-all"></span>
                      {t('nav.challenges')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/community" className="font-mono uppercase tracking-wide text-xs group hover:text-primary">
                      <span className="inline-block w-1 h-1 bg-primary mr-1 group-hover:w-2 transition-all"></span>
                      {t('nav.community')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/ranking" className="font-mono uppercase tracking-wide text-xs group hover:text-primary">
                      <span className="inline-block w-1 h-1 bg-primary mr-1 group-hover:w-2 transition-all"></span>
                      {t('nav.ranking')}
                    </Link>
                  </li>
                </ul>
              </div>
            )}
            <Link 
              to="/" 
              className={`btn btn-ghost btn-sm normal-case text-primary font-mono font-bold tracking-wider p-1 glitch transition-all duration-300 ${
                scrolled ? 'text-xs sm:text-sm' : 'text-sm sm:text-xl'
              }`}
            >
              <span className="hidden sm:inline drop-shadow-glow">PROGRAME.TECH</span>
              <span className="sm:hidden drop-shadow-glow">P.TECH</span>
            </Link>
          </div>

          <div className="navbar-center hidden lg:flex">
            {user && (
              <div className="flex gap-6">
                <Link 
                  to="/learn" 
                  className="relative group font-mono uppercase tracking-wide text-xs font-bold hover:text-primary transition-colors"
                >
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
                  {t('nav.learn')}
                </Link>
                <Link 
                  to="/challenges" 
                  className="relative group font-mono uppercase tracking-wide text-xs font-bold hover:text-primary transition-colors"
                >
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
                  {t('nav.challenges')}
                </Link>
                <Link 
                  to="/community" 
                  className="relative group font-mono uppercase tracking-wide text-xs font-bold hover:text-primary transition-colors"
                >
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
                  {t('nav.community')}
                </Link>
                <Link 
                  to="/ranking" 
                  className="relative group font-mono uppercase tracking-wide text-xs font-bold hover:text-primary transition-colors"
                >
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
                  {t('nav.ranking')}
                </Link>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className={`transition-all duration-300 ${scrolled ? 'scale-75' : 'scale-75 sm:scale-100'}`}>
              <ThemeSwitcher />
            </div>
            {user ? (
              <div className="dropdown dropdown-end">
                <label tabIndex={0} className="btn btn-ghost btn-sm btn-circle avatar p-0 relative group">
                  {/* Avatar glow on hover */}
                  <div className="absolute -inset-1 rounded-full bg-primary/20 blur-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className={`rounded-full ring-2 ring-primary/80 ring-offset-base-100 ring-offset-2 shadow-[0_0_10px_rgba(138,75,255,0.3)] group-hover:shadow-[0_0_15px_rgba(138,75,255,0.5)] transition-all relative z-10 ${
                    scrolled ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-7 h-7 sm:w-8 sm:h-8'
                  }`}>
                    {userProfile?.avatar_url ? (
                      <img 
                        src={userProfile.avatar_url} 
                        alt={userProfile.username || 'User avatar'} 
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${user.email}`;
                        }}
                      />
                    ) : (
                      <img 
                        src={`https://api.dicebear.com/7.x/bottts/svg?seed=${user.email}`} 
                        alt="Default avatar" 
                      />
                    )}
                  </div>
                </label>
                <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[60] p-3 shadow-lg bg-base-200/90 backdrop-blur-md rounded-box w-52 border border-primary/40 overflow-hidden">
                  {/* Dropdown decoration */}
                  <div className="absolute top-0 left-1/4 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-primary/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent"></div>
                  
                  {/* Dropdown scanlines */}
                  <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(138,75,255,0.05)_50%)] bg-[length:100%_3px] opacity-10 pointer-events-none"></div>
                  
                  <li>
                    <Link to="/profile" className="justify-between font-mono text-xs uppercase tracking-wide group hover:bg-primary/10">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-1 h-1 bg-primary group-hover:w-2 transition-all"></span>
                        {t('nav.profile')}
                      </div>
                      {userProfile?.username && (
                        <span className="text-xs opacity-70 truncate max-w-20">{userProfile.username}</span>
                      )}
                    </Link>
                  </li>
                  <li>
                    <button onClick={signOut} className="font-mono text-xs uppercase tracking-wide group hover:bg-primary/10">
                      <span className="inline-block w-1 h-1 bg-primary group-hover:w-2 transition-all"></span>
                      {t('nav.logout')}
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="btn btn-xs sm:btn-sm bg-transparent border border-primary/40 hover:border-primary hover:bg-primary/10 text-primary font-mono uppercase tracking-wide text-xs shadow-[0_0_10px_rgba(138,75,255,0.2)] hover:shadow-[0_0_15px_rgba(138,75,255,0.4)]"
              >
                Login
              </Link>
            )}
          </div>
        </div>
        
        {/* Improved bottom border with less opacity for better contrast */}
        <div className="h-[1px] w-full bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20"></div>
      </header>

      {/* Updated spacer with consistent height */}
      <div className={`w-full ${
        isTutorialPage 
          ? 'h-0' 
          : scrolled 
            ? 'h-[3rem]' 
            : 'h-[4rem]'
      } transition-all duration-300`}></div>

      {/* Scroll to top button */}
      {showScrollButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 z-50 btn btn-circle btn-sm sm:btn-md btn-primary shadow-lg shadow-primary/20 animate-bounce"
          aria-label="Scroll to top"
        >
          <FiChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      )}
      
    </>
  );
}